import type { CalendarBarColorConfig } from '../../../config/calendarColumnsConfig';

export interface ResolvedBarColors {
    label: string;
    color: string;
    bgColor: string;
}

/**
 * Only for `colorField` values the customer config's `colorMap` does not declare
 * (e.g. a promo type added backend-side before anyone curated a colour). Keeps
 * such bars visually distinct instead of collapsing them into one shade; the
 * curated `colorMap` remains the source of truth for known values.
 */
const FALLBACK_PALETTE: readonly Omit<ResolvedBarColors, 'label'>[] = [
    { color: '#6366F1', bgColor: '#EEF2FF' },
    { color: '#EC4899', bgColor: '#FDF2F8' },
    { color: '#14B8A6', bgColor: '#F0FDFA' },
    { color: '#F59E0B', bgColor: '#FFFBEB' },
    { color: '#8B5CF6', bgColor: '#F5F3FF' },
    { color: '#EF4444', bgColor: '#FEF2F2' },
    { color: '#10B981', bgColor: '#ECFDF5' },
    { color: '#3B82F6', bgColor: '#EFF6FF' },
];

/** Used when a row carries no `colorField` value at all. */
const EMPTY_VALUE_COLORS: ResolvedBarColors = { label: '', color: '#A1A1AA', bgColor: '#F4F4F5' };

const normalizeKey = (value: string): string => value.trim().toLowerCase();

function hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
}

/**
 * Lower-cased index over the configured `colorMap` so a config authored with
 * backend keys still matches a display-cased API value.
 */
export function buildBarColorIndex(
    colorMap: Record<string, CalendarBarColorConfig> | undefined,
): Map<string, CalendarBarColorConfig> {
    const index = new Map<string, CalendarBarColorConfig>();
    if (!colorMap) return index;
    for (const [key, colors] of Object.entries(colorMap)) {
        const normalized = normalizeKey(key);
        if (normalized) index.set(normalized, colors);
    }
    return index;
}

// Dev-only: warn once per value so an uncurated promo type is noticed without
// flooding the console on every render pass.
const warnedValues = new Set<string>();

function warnUnmapped(value: string): void {
    if (process.env.NODE_ENV === 'production' || warnedValues.has(value)) return;
    warnedValues.add(value);
    // eslint-disable-next-line no-console
    console.warn(
        `[calendarBarColors] "${value}" is not in calendarBarFieldMap.colorMap — using a generated fallback colour.`,
    );
}

export function resolveBarColors(
    value: string | null | undefined,
    colorIndex: Map<string, CalendarBarColorConfig>,
): ResolvedBarColors {
    if (!value) return EMPTY_VALUE_COLORS;

    const normalized = normalizeKey(value);
    const configured = colorIndex.get(normalized);
    if (configured) {
        return { label: configured.label, color: configured.color, bgColor: configured.bgColor };
    }

    warnUnmapped(value);
    const fallback = FALLBACK_PALETTE[hashString(normalized) % FALLBACK_PALETTE.length];
    return { label: '', ...fallback };
}
